import { c as _c } from "react-compiler-runtime";
import { useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import styled from 'foundations-theming/utils';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { EditColumnsIcon } from 'foundations-assets/icons/components/EditColumnsIcon';
import { PinIcon } from 'foundations-assets/icons/components/PinIcon';
import { UnpinnedIcon } from 'foundations-assets/icons/components/UnpinnedIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { THEAD_Z_INDEX } from '../table/components/StyledTableComponents';
import { TABLE_RIGHT_TOOLBAR_WIDTH } from '../table/constants/sizes';
import { useAnimateExpandedState } from './useAnimateExpandedState';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CircularHover = styled.span.withConfig({
  displayName: "RightToolbarRail__CircularHover"
})(["display:inline-flex;border-radius:50%;&:hover,&:has(:focus-visible){background-color:", ";}"], ({
  theme
}) => theme.color['fill-field-hover']);
const PinCircularHover = styled.span.withConfig({
  displayName: "RightToolbarRail__PinCircularHover"
})(["display:inline-flex;border-radius:50%;", " &:hover,&:has(:focus-visible){background-color:", ";}"], ({
  theme,
  $isPinned
}) => $isPinned && `background-color: ${theme.color['fill-field-hover']};`, ({
  theme
}) => theme.color['fill-field-hover']);
const AnimatedRail = styled.div.withConfig({
  displayName: "RightToolbarRail__AnimatedRail"
})(["position:absolute;inset-inline-end:0;inset-block-start:0;inset-block-end:0;inline-size:", "px;overflow:hidden;z-index:", ";transition:inline-size 150ms ease;"], ({
  $width
}) => $width, THEAD_Z_INDEX + 1);
const RightToolbarRail = t0 => {
  const $ = _c(32);
  const {
    config,
    width,
    headerHeight,
    isToolbarPinnable,
    isPinned: t1,
    onTogglePin
  } = t0;
  const isPinned = t1 === undefined ? true : t1;
  const [addColumnTooltipOpen, setAddColumnTooltipOpen] = useState(false);
  const [editColumnsTooltipOpen, setEditColumnsTooltipOpen] = useState(false);
  const [pinTooltipOpen, setPinTooltipOpen] = useState(false);
  const [isPinHovered, setIsPinHovered] = useState(false);
  const t2 = isPinned !== null && isPinned !== void 0 ? isPinned : false;
  let t3;
  if ($[0] !== t2 || $[1] !== width) {
    t3 = {
      isPinned: t2,
      width
    };
    $[0] = t2;
    $[1] = width;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    visibleWidth,
    contentVisible,
    handleTransitionEnd,
    handleMouseEnter,
    handleMouseLeave,
    notifyWillUnpin
  } = useAnimateExpandedState(t3);
  let t4;
  if ($[3] !== config) {
    t4 = e => {
      e.stopPropagation();
      setAddColumnTooltipOpen(false);
      config.onAddColumn(e.currentTarget);
    };
    $[3] = config;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleAddColumn = t4;
  let t5;
  if ($[5] !== config) {
    t5 = e_0 => {
      e_0.stopPropagation();
      setEditColumnsTooltipOpen(false);
      config.onEditColumns(e_0.currentTarget);
    };
    $[5] = config;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleEditColumns = t5;
  let t6;
  if ($[7] !== isPinned || $[8] !== notifyWillUnpin || $[9] !== onTogglePin) {
    t6 = e_1 => {
      e_1.stopPropagation();
      setPinTooltipOpen(false);
      if (isPinned) {
        notifyWillUnpin();
      }
      onTogglePin === null || onTogglePin === void 0 || onTogglePin();
    };
    $[7] = isPinned;
    $[8] = notifyWillUnpin;
    $[9] = onTogglePin;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleTogglePin = t6;
  let t7;
  if ($[11] !== addColumnTooltipOpen || $[12] !== config.editColumnsShortcut || $[13] !== contentVisible || $[14] !== editColumnsTooltipOpen || $[15] !== handleAddColumn || $[16] !== handleEditColumns || $[17] !== handleTogglePin || $[18] !== headerHeight || $[19] !== isPinHovered || $[20] !== isPinned || $[21] !== isToolbarPinnable || $[22] !== pinTooltipOpen) {
    t7 = contentVisible && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        blockSize: headerHeight,
        flexShrink: 0,
        borderBlockEndWidth: "100",
        borderBlockEndStyle: "solid",
        borderBlockEndColor: "border-core-subtle",
        children: /*#__PURE__*/_jsx(Tooltip, {
          open: addColumnTooltipOpen,
          onOpenChange: evt => setAddColumnTooltipOpen(evt.currentTarget.value),
          placement: "left",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "datatable.rightToolbar.addColumn"
          }),
          children: /*#__PURE__*/_jsx(CircularHover, {
            children: /*#__PURE__*/_jsx(IconButton, {
              "aria-label": I18n.text("datatable.rightToolbar.addColumn"),
              size: "extra-small",
              use: "transparent",
              onClick: handleAddColumn,
              children: /*#__PURE__*/_jsx(AddIcon, {})
            })
          })
        })
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "center",
        paddingBlockStart: "100",
        flexGrow: 1,
        children: /*#__PURE__*/_jsx(Tooltip, {
          open: editColumnsTooltipOpen,
          onOpenChange: evt_0 => setEditColumnsTooltipOpen(evt_0.currentTarget.value),
          placement: "left",
          title: config.editColumnsShortcut ? /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            alignItems: "center",
            gap: "50",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "datatable.rightToolbar.editColumns"
            }), config.editColumnsShortcut]
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "datatable.rightToolbar.editColumns"
          }),
          children: /*#__PURE__*/_jsx(CircularHover, {
            children: /*#__PURE__*/_jsx(IconButton, {
              "aria-label": I18n.text("datatable.rightToolbar.editColumns"),
              size: "extra-small",
              use: "transparent",
              onClick: handleEditColumns,
              children: /*#__PURE__*/_jsx(EditColumnsIcon, {})
            })
          })
        })
      }), isToolbarPinnable && /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        paddingBlockEnd: "100",
        children: /*#__PURE__*/_jsx(Tooltip, {
          open: pinTooltipOpen,
          onOpenChange: evt_1 => setPinTooltipOpen(evt_1.currentTarget.value),
          placement: "left",
          title: isPinned ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "datatable.rightToolbar.unpinToolbar"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "datatable.rightToolbar.pinToolbar"
          }),
          children: /*#__PURE__*/_jsx(PinCircularHover, {
            $isPinned: isPinned,
            onMouseEnter: () => setIsPinHovered(true),
            onMouseLeave: () => setIsPinHovered(false),
            children: /*#__PURE__*/_jsx(IconButton, {
              "aria-label": isPinned ? I18n.text("datatable.rightToolbar.unpinToolbar") : I18n.text("datatable.rightToolbar.pinToolbar"),
              size: "extra-small",
              use: "transparent",
              onClick: handleTogglePin,
              children: isPinned && isPinHovered ? /*#__PURE__*/_jsx(UnpinnedIcon, {
                "data-test-id": "right-toolbar-unpin-icon"
              }) : /*#__PURE__*/_jsx(PinIcon, {
                "data-test-id": "right-toolbar-pin-icon"
              })
            })
          })
        })
      })]
    });
    $[11] = addColumnTooltipOpen;
    $[12] = config.editColumnsShortcut;
    $[13] = contentVisible;
    $[14] = editColumnsTooltipOpen;
    $[15] = handleAddColumn;
    $[16] = handleEditColumns;
    $[17] = handleTogglePin;
    $[18] = headerHeight;
    $[19] = isPinHovered;
    $[20] = isPinned;
    $[21] = isToolbarPinnable;
    $[22] = pinTooltipOpen;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t7) {
    t8 = /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      blockSize: "100%",
      inlineSize: `${TABLE_RIGHT_TOOLBAR_WIDTH}px`,
      backgroundColor: "fill-surface-default",
      borderInlineStartWidth: "100",
      borderInlineStartStyle: "solid",
      borderInlineStartColor: "border-core-subtle",
      children: t7
    });
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== handleMouseEnter || $[27] !== handleMouseLeave || $[28] !== handleTransitionEnd || $[29] !== t8 || $[30] !== visibleWidth) {
    t9 = /*#__PURE__*/_jsx(AnimatedRail, {
      "data-test-id": "right-toolbar-rail",
      $width: visibleWidth,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onTransitionEnd: handleTransitionEnd,
      children: t8
    });
    $[26] = handleMouseEnter;
    $[27] = handleMouseLeave;
    $[28] = handleTransitionEnd;
    $[29] = t8;
    $[30] = visibleWidth;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
};
export default RightToolbarRail;